import { Formik, Form } from 'formik'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/Formik/SelectInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import MwMLoader from '~/components/MwMLoader'
import { statusOptions } from '~/const/asset'
import { useAssetDetailsQuery, useBatchStatusUpdateMutation } from '~/queries/assets'
import { getStatusOptions } from '~/utils/asset'
import { reactSelectMenuPositionDef } from '~/utils/options'

import './styles.scss'

const MODAL_ID = 'change-status-modal'

interface FormProps {
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
  selectedAssetsIds: string[]
}

interface Values {
  status: IOption | undefined
}

const ChangeStatusModalForm = ({ onClose, onFailure, onSuccess, selectedAssetsIds }: FormProps): ReactElement => {
  const { mutateAsync: batchUpdate } = useBatchStatusUpdateMutation()

  const { data: assetData, isLoading } = useAssetDetailsQuery(selectedAssetsIds[0])

  const handleSubmit = async (values: Values) => {
    const { status } = values

    try {
      if (status) {
        const updateData = {
          assets_status: selectedAssetsIds.reduce((obj, key) => Object.assign(obj, { [key]: status.value }), {}),
        }

        await batchUpdate(updateData)
        onSuccess()
      }
    } catch {
      onFailure()
    }
  }

  const initialValues = {
    status: statusOptions.find(({ value }) => value === assetData?.status),
  }

  return isLoading ? (
    <MwMLoader category="item" />
  ) : (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ isSubmitting }) => (
        <Form>
          <ModalHeader>Update assets ({selectedAssetsIds.length})</ModalHeader>
          <ModalBody>
            <div className="status-sub-title">Change status</div>
            <SelectInput
              {...reactSelectMenuPositionDef(MODAL_ID)}
              defaultValue={initialValues.status}
              label="Status"
              name="status"
              options={getStatusOptions(assetData?.status)}
              placeholder="Select a status"
            />
          </ModalBody>
          <ModalFooter>
            <Button ghost onClick={onClose} type="button">
              Cancel
            </Button>
            <Button disabled={isSubmitting} type="submit">
              Save
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  )
}

interface Props {
  isOpen: boolean
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
  selectedAssetsIds: string[]
}

const ChangeStatusModal = ({ isOpen, onClose, onFailure, onSuccess, selectedAssetsIds }: Props): ReactElement => {
  return (
    <Modal id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ChangeStatusModalForm
        onClose={onClose}
        onFailure={onFailure}
        onSuccess={onSuccess}
        selectedAssetsIds={selectedAssetsIds}
      />
    </Modal>
  )
}

export default ChangeStatusModal
